import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import { GRAPH_HEIGHT } from './index';
import { Nav } from 'react-bootstrap-5';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import isEqual from 'lodash/isEqual';
import { parseWithFormats } from 'helpers/date_utils';
import { NonBasicCardStyling } from './util';

export type Metric = {
  dailyData: Record<string, number>;
  aggregateValue: number;
}

export type Props = {
  websiteViews: Metric;
  timeOnPage: Metric;
  uniqueVisitors: Metric;
  filters?: {
    chartDataToShow?: number;
  };
  onFilterUpdate?: (filters: any) => void;
  preview?: boolean;
};

enum ChartOptions {
  Views,
  UniqueVisitors,
  TimeOnPage,
}

export default function WebsiteTraffic({
  timeOnPage,
  uniqueVisitors,
  websiteViews,
  filters = {},
  onFilterUpdate,
  preview = false,
}: Props) {
  const [chartDataToShow, setChartDataToShow] = useState<ChartOptions>(
    filters.chartDataToShow !== undefined ? filters.chartDataToShow : ChartOptions.Views
  );

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref.current;
  }

  const prevFilters = usePrevious(filters);

  useEffect(() => {
    if (prevFilters && !isEqual(prevFilters, filters)) {
      if (filters.chartDataToShow !== undefined) {
        setChartDataToShow(filters.chartDataToShow);
      }
    }
  }, [filters, prevFilters]);

  const handleTabChange = (key: ChartOptions) => {
    setChartDataToShow(key);

    if (!preview && onFilterUpdate) {
      onFilterUpdate({
        chartDataToShow: key
      });
    }
  };

  function hasData(): boolean {
    return Object.keys(timeOnPage.dailyData).length > 0 && Object.keys(uniqueVisitors.dailyData).length > 0
      && Object.keys(websiteViews.dailyData).length > 0;
  }

  function datasetToUse(): { dataset: Record<string, number>, datasetLabel: string } {
    const sanitize = (data: Record<string, number> | undefined) => {
      if (!data) return {};
      return Object.entries(data).reduce((acc, [key, value]) => {
        acc[key] = value ?? 0;
        return acc;
      }, {} as Record<string, number>);
    };

    if (chartDataToShow === ChartOptions.UniqueVisitors) {
      return { dataset: sanitize(uniqueVisitors.dailyData), datasetLabel: 'Unique Visits for day' };
    } else if (chartDataToShow === ChartOptions.TimeOnPage) {
      return { dataset: sanitize(timeOnPage.dailyData), datasetLabel: 'Avg. Time on page for day' };
    }

    return { dataset: sanitize(websiteViews.dailyData), datasetLabel: 'Views for day' };
  }

  const { dataset, datasetLabel } = datasetToUse();
  const data = {
    labels: Object.keys(dataset).map((stringDate) => {
      const date = parseWithFormats(stringDate, 'YYYYMMDD');
      return date.toISOString().slice(0, 10);
    }),
    datasets: [
      {
        label: datasetLabel,
        data: Object.values(dataset),
        borderColor: '#2968E7',
        fill: false,
        pointBackgroundColor: '#99C25B',
        pointBorderColor: '#99C25B',
        pointRadius: 3,
        pointBorderWidth: 0,
        tension: 0
      }
    ]
  };

  const graphOptions = {
    maintainAspectRatio: false,
    legend: { display: false, },
    tooltips: {
      displayColors: false,
    },
    scales: {
      xAxes: [{
        type: 'time',
        time: {
          parser: 'YYYY-MM-DD',
          unit: 'day',
          tooltipFormat: 'MMM DD YYYY',
          displayFormats: {
            day: 'MM/DD',
            month: 'MM/DD'
          },
        },
        ticks: {
          source: 'data',
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          maxTicksLimit: 7
        },
        gridLines: {
          display: false,
          offset: false
        },
      }],
      yAxes: [{
        stacked: true,
        ticks: {
          beginAtZero: true,
          maxTicksLimit: 7
        },
        gridLines: {
          color: '#D8DADF',
          drawBorder: false,
          zeroLineColor: '#D8DADF'
        },
      }]
    }
  };

  return (
    <div className={NonBasicCardStyling}>
      <div className="d-flex justify-content-between align-items-center">
        <h6 className="m-0 fw-normal">Website Traffic</h6>
      </div>

      {hasData() ? (
        <>
          <Nav
            activeKey={chartDataToShow}
            className="nav-justified"
            id="website-traffic-tabs"
            variant="tabs"
            onSelect={(key: any) => handleTabChange(key)}
          >
            <Nav.Item>
              <Nav.Link className="justify-content-center" eventKey={ChartOptions.Views}>
                <TabTitleHtml subtitle="Views" title={websiteViews.aggregateValue.toString()}/>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link className="justify-content-center" eventKey={ChartOptions.UniqueVisitors}>
                <TabTitleHtml subtitle="Unique Visitors" title={uniqueVisitors.aggregateValue.toString()}/>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link className="justify-content-center" eventKey={ChartOptions.TimeOnPage}>
                <TabTitleHtml subtitle="Avg. Time on Page" title={`${timeOnPage.aggregateValue ?? 0}s`}/>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div style={{ height: `${GRAPH_HEIGHT}px`, overflow: 'hidden', padding: '10px 0' }}>
            <Line data={data} height={GRAPH_HEIGHT} options={graphOptions}/>
          </div>
        </>
      ) : (
        <Empty className="bg-white" style={{ minHeight: '250px' }}>
          <Empty.Media>
            <FontAwesomeIcon icon={faExclamationCircle} />
          </Empty.Media>
          <Empty.Title>No website data to display</Empty.Title>
          <Empty.Content>Waiting For Data...</Empty.Content>
        </Empty>
      )}
    </div>
  );
}

function TabTitleHtml({ title, subtitle }: { title: string, subtitle: string }) {
  return (
    <div className="text-center">
      <div className="fs-large fw-semibold">{title}</div>
      <div className="text-muted">{subtitle}</div>
    </div>
  );
}
